import React, { useEffect, useState, useCallback } from "react";
import { API_BASE } from "@/App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import IntelligencePanel from "@/components/IntelligencePanel";
import { ArrowLeft, FileText, PenLine, ScrollText, Loader2, Inbox } from "lucide-react";

const SUITE_LABELS = {
  GS1: "GS-I", GS2: "GS-II", GS3: "GS-III", GS4: "GS-IV", ESSAY: "Essay",
};
const FILTERS = [
  { key: "", label: "All" },
  { key: "GS1", label: "GS-I" },
  { key: "GS2", label: "GS-II" },
  { key: "GS3", label: "GS-III" },
  { key: "GS4", label: "GS-IV" },
  { key: "ESSAY", label: "Essay" },
];

const fmtScore = (score, marks) => {
  if (score === null || score === undefined) return "—";
  if (marks) return `${((score / marks) * 10).toFixed(1)}/10`;
  return `${score}`;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
};

const ModeIcon = ({ mode }) =>
  mode === "handwritten" ? <PenLine className="w-3.5 h-3.5" /> :
  mode === "essay" ? <ScrollText className="w-3.5 h-3.5" /> :
  <FileText className="w-3.5 h-3.5" />;

export default function MyEvaluationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [suite, setSuite] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_BASE}/eval/history?limit=50${suite ? `&suite=${suite}` : ""}`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const d = await res.json();
        setItems(d.items || []);
        setTotal(d.total || 0);
      }
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [suite]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const res = await fetch(`${API_BASE}/eval/history/${id}`, { headers });
      if (res.ok) setDetail(await res.json());
      else setDetail(null);
    } catch (e) { setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const fb = detail?.feedback || {};

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="my-evaluations-page">
      <button
        onClick={() => navigate("/dashboard")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"
        data-testid="evaluations-back-button"
      >
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </button>

      <div className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">My Evaluations</h1>
        <p className="text-sm text-slate-500 mt-1">
          Every answer you've had evaluated — question, your answer & feedback, saved for you to revisit anytime.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setSuite(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              suite === f.key ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`evaluations-filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400" data-testid="evaluations-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed" data-testid="evaluations-empty">
          <CardContent className="py-14 flex flex-col items-center text-center">
            <Inbox className="w-10 h-10 text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No evaluations yet</p>
            <p className="text-sm text-slate-400 mt-1">Get an answer evaluated and it will show up here.</p>
            <button
              onClick={() => navigate("/mains")}
              className="mt-4 px-4 py-2 rounded-full bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
              data-testid="evaluations-empty-cta"
            >
              Evaluate an answer
            </button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="evaluations-list">
          <p className="text-xs text-slate-400">{total} saved evaluation{total === 1 ? "" : "s"}</p>
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => openDetail(it.id)}
              className="w-full text-left"
              data-testid={`evaluation-card-${it.id}`}
            >
              <Card className="border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all">
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                        <ModeIcon mode={it.mode} /> {SUITE_LABELS[it.suite] || it.suite}
                      </span>
                      <span className="text-[11px] text-slate-400 capitalize">{it.mode}</span>
                      <span className="text-[11px] text-slate-400">· {fmtDate(it.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-2">{it.question_preview || "Untitled question"}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-lg font-bold text-emerald-700">{fmtScore(it.score, it.marks)}</div>
                    <div className="text-[10px] text-slate-400">score</div>
                  </div>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="evaluation-detail-dialog">
          {detailLoading || detail?.loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : detail ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-slate-800">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                    <ModeIcon mode={detail.mode} /> {SUITE_LABELS[detail.suite] || detail.suite}
                  </span>
                  <span className="text-xs font-normal text-slate-400">{fmtDate(detail.created_at)}</span>
                </DialogTitle>
              </DialogHeader>

              {/* Score */}
              {detail.score !== null && detail.score !== undefined && (
                <div className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                  <span className="text-sm font-medium text-emerald-800">Estimated Score</span>
                  <span className="text-2xl font-bold text-emerald-700" data-testid="detail-score">
                    {fmtScore(detail.score, detail.marks)}
                  </span>
                </div>
              )}

              {/* Question */}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Question</p>
                <p className="text-sm text-slate-800" data-testid="detail-question">{detail.question}</p>
              </div>

              {/* Your answer */}
              {detail.answer && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">
                    Your answer <span className="text-slate-300">· {detail.word_count} words</span>
                  </p>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-100" data-testid="detail-answer">
                    {detail.answer}
                  </p>
                </div>
              )}

              {/* Knowledge Graph + Strengthen block */}
              {fb.intelligence && (
                <IntelligencePanel
                  intelligence={fb.intelligence}
                  gaps={{ examples: fb.missing_examples, keywords: fb.keyword_suggestions }}
                />
              )}

              {/* Structure & Policy Logic */}
              {fb.structure_feedback?.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                  <p className="font-semibold text-sm text-slate-800 mb-2">Structure & Policy Logic</p>
                  <ul className="space-y-2">
                    {fb.structure_feedback.map((p, i) => (
                      <li key={i} className="text-sm text-slate-600 flex gap-2">
                        <span className="text-indigo-400">•</span>
                        <span>{p.comment || p.aspect}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Depth & Interlinkages */}
              {fb.depth_feedback?.length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                  <p className="font-semibold text-sm text-slate-800 mb-2">Depth & Interlinkages</p>
                  <ul className="space-y-2">
                    {fb.depth_feedback.map((p, i) => (
                      <li key={i} className="text-sm text-slate-600 flex gap-2">
                        <span className="text-emerald-500">•</span>
                        <span>{p.comment || p.aspect}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Meta tips */}
              {fb.meta_tips?.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                  <p className="font-semibold text-sm text-slate-800 mb-2">Tips</p>
                  <ul className="space-y-1.5">
                    {fb.meta_tips.map((t, i) => (
                      <li key={i} className="text-sm text-slate-600 flex gap-2">
                        <span className="text-amber-500">•</span><span>{typeof t === "string" ? t : t.comment}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Essay-style feedback fallback */}
              {(fb.strengths?.length > 0 || fb.weaknesses?.length > 0 || fb.summary) && (
                <div className="space-y-3">
                  {fb.summary && <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{fb.summary}</p>}
                  {fb.strengths?.length > 0 && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                      <p className="font-semibold text-sm text-slate-800 mb-2">Strengths</p>
                      <ul className="space-y-1.5">
                        {fb.strengths.map((s, i) => (
                          <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-emerald-500">•</span>{typeof s === "string" ? s : s.point}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {fb.weaknesses?.length > 0 && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4">
                      <p className="font-semibold text-sm text-slate-800 mb-2">Areas to improve</p>
                      <ul className="space-y-1.5">
                        {fb.weaknesses.map((w, i) => (
                          <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-rose-400">•</span>{typeof w === "string" ? w : w.point}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500 py-10 text-center">Couldn't load this evaluation.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
